import { Dialog, PlainButton } from "@jugl-web/ui-components";
import { Button, TextInput } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import React, { useState } from "react";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ShopifyIcon } from "./assets/shopify.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export interface ShopifyStorePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (store: string) => void;
}

const SHOPIFY_SUFFIX = ".myshopify.com";
const STORE_REGEX = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

// Accepts inputs like "jugl-dev", "jugl-dev.myshopify.com", or
// "https://jugl-dev.myshopify.com/" and returns the canonical
// "jugl-dev.myshopify.com" form (or null if it can't be coerced into one).
const normalizeStoreDomain = (input: string): string | null => {
  let value = input.trim().toLowerCase();
  value = value.replace(/^https?:\/\//, "");
  value = value.replace(/\/.*$/, "");
  if (!value) return null;
  if (!value.endsWith(SHOPIFY_SUFFIX)) {
    value = `${value}${SHOPIFY_SUFFIX}`;
  }
  return STORE_REGEX.test(value) ? value : null;
};

export const ShopifyStorePromptDialog: React.FC<
  ShopifyStorePromptDialogProps
> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslations();
  const [store, setStore] = useState("");
  const [isInvalid, setIsInvalid] = useState(false);

  const handleClose = () => {
    setStore("");
    setIsInvalid(false);
    onClose();
  };

  const handleContinue = () => {
    const normalized = normalizeStoreDomain(store);
    if (!normalized) {
      setIsInvalid(true);
      return;
    }
    onSubmit(normalized);
    setStore("");
    setIsInvalid(false);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      className="flex w-[520px] flex-col overflow-hidden rounded-2xl"
    >
      <div className="border-dark-100 flex h-[60px] shrink-0 items-center justify-between border-0 border-b border-solid px-6">
        <div className="flex items-center gap-3">
          <ShopifyIcon className="h-6 w-6" />
          <span className="font-secondary font-semibold text-[#383838]">
            {t({
              id: "cx-actions-page.shopify-store-prompt-title",
              defaultMessage: "Connect Shopify Store",
            })}
          </span>
        </div>
        <PlainButton
          onClick={handleClose}
          className="flex items-center justify-center"
        >
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="flex flex-col gap-6 px-8 py-8">
        <span className="font-secondary text-dark-600 text-sm">
          {t({
            id: "cx-actions-page.shopify-store-prompt-description",
            defaultMessage:
              "Enter your Shopify store domain to begin installation. You'll be redirected to Shopify to approve the connection.",
          })}
        </span>
        <TextInput
          value={store}
          onChange={(event) => {
            setStore(event.target.value.trimStart());
            if (isInvalid) setIsInvalid(false);
          }}
          isInvalid={isInvalid}
          errorMessage={
            isInvalid
              ? t({
                  id: "cx-actions-page.shopify-store-prompt-invalid",
                  defaultMessage:
                    "Enter a valid Shopify store domain (e.g. your-store.myshopify.com)",
                })
              : undefined
          }
          label={t({
            id: "cx-actions-page.shopify-store-prompt-label",
            defaultMessage: "Store domain",
          })}
          labelPosition="border-overlap"
          placeholder="your-store.myshopify.com"
        />
      </div>
      <div className="border-dark-100 flex shrink-0 justify-end gap-3 border-0 border-t border-solid px-6 py-4">
        <Button variant="tertiary" onClick={handleClose} className="w-[140px]">
          {t({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        <Button
          onClick={handleContinue}
          isDisabled={!store.trim()}
          iconEnd={<ArrowRightIcon />}
          className="w-[180px]"
        >
          {t({
            id: "cx-actions-page.shopify-store-prompt-continue",
            defaultMessage: "Continue",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
